// src/App.js
import React, { useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import Analytics from './utils/analytics';
import Monitoring from './utils/monitoring';
import PageTitle from './components/utils/PageTitle';
import InstallPrompt from './components/PWA/InstallPrompt';

// Lazy load all pages for code splitting
const LandingPage = React.lazy(() => import('./pages/Landing/LandingPage'));
const SignUpPage = React.lazy(() => import('./pages/SignUp/SignUpPage'));
const LoginPage = React.lazy(() => import('./pages/Login/LoginPage'));
const DashboardPage = React.lazy(() => import('./pages/Dashboard/DashboardPage'));
const LearnMorePage = React.lazy(() => import('./pages/LearnMore/LearnMorePage'));
const ExploreTribesPage = React.lazy(() => import('./pages/Tribes/ExploreTribesPage'));
const CreateTribePage = React.lazy(() => import('./pages/Tribes/CreateTribePage'));
const DetailPage = React.lazy(() => import('./pages/Tribes/DetailPage'));
const PublicProfilePage = React.lazy(() => import('./pages/Profile/PublicProfilePage'));
const ExplorePeoplePage = React.lazy(() => import('./pages/Explore/ExplorePeoplePage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/Login/ForgotPasswordPage'));
const ProjectMatchmakingPage = React.lazy(() => import('./pages/ProjectMatchmaking/ProjectMatchmakingPage'));
const ProjectDetailsPage = React.lazy(() => import('./pages/ProjectMatchmaking/ProjectDetailsPage'));

// Loading component
const LoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
    <div className="flex flex-col items-center space-y-4">
      <div className="relative">
        <div className="w-12 h-12 border-4 border-blue-500/20 rounded-full animate-spin">
          <div className="absolute top-0 left-0 w-12 h-12 border-4 border-blue-500 rounded-full animate-spin" 
               style={{clipPath: 'polygon(0% 0%, 100% 0%, 100% 25%, 0% 25%)'}}></div>
        </div>
      </div>
      <p className="text-white/60 text-sm">Loading...</p>
    </div>
  </div>
);

function App() {
  useEffect(() => {
    // Initialize monitoring and analytics
    Monitoring.init();
    Analytics.init();
    
    // Track initial page load
    Analytics.trackPageView('App Load', '/');
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <PageTitle />
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/signup" element={<SignUpPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/learn-more" element={<LearnMorePage />} />
              <Route path="/tribes" element={<ExploreTribesPage />} />
              <Route path="/tribes/create" element={<CreateTribePage />} />
              <Route path="/tribes/:tribeId" element={<DetailPage />} />
              <Route path="/profile/:userId" element={<PublicProfilePage />} />
              <Route path="/explore" element={<ExplorePeoplePage />} />
              <Route path="/project-matchmaking" element={<ProjectMatchmakingPage />} />
              <Route path="/project-matchmaking/:projectId" element={<ProjectDetailsPage />} />
            </Routes>
          </Suspense>
          <InstallPrompt />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;